import { readonly, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ThemePreference } from '@/config/constants';
import logger from '@/services/logger';

/** Class on <html> that switches Ionic, PrimeVue and the app's CSS to dark. */
export const DARK_PALETTE_CLASS = 'ion-palette-dark';

// A copy of the saved preference that can be read synchronously at startup,
// before the store has been hydrated from its asynchronous storage, so the
// first paint is already in the chosen palette.
const PREFERENCE_STORAGE_KEY = 'themePreference';

const darkMode = ref(false);
let preference = ThemePreference.LIGHT;
let systemQuery: MediaQueryList | undefined;

/** Whether the dark palette is currently applied. */
export const isDarkMode = readonly(darkMode);

// Any value that is not a preference, such as none saved yet or one saved
// under a name since changed, is light
const toPreference = (value: unknown): ThemePreference =>
  Object.values(ThemePreference).includes(value as ThemePreference)
    ? (value as ThemePreference)
    : ThemePreference.LIGHT;

const readStoredPreference = (): ThemePreference => {
  try {
    return toPreference(localStorage.getItem(PREFERENCE_STORAGE_KEY));
  } catch {
    return ThemePreference.LIGHT;
  }
};

const storePreference = (next: ThemePreference) => {
  try {
    localStorage.setItem(PREFERENCE_STORAGE_KEY, next);
  } catch {
    // Unavailable storage only costs the startup copy: the store still holds
    // the preference and applies it once hydrated.
  }
};

// The mobile browser's address bar and the native status bar are drawn
// outside the page, so they are told the palette separately.
const syncSurroundings = (dark: boolean) => {
  const background = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-background-color')
    .trim();
  if (background) {
    let meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = background;
  }

  if (Capacitor.isNativePlatform()) {
    // Style.Dark is light text, for a dark background
    StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(
      (e: any) => logger.warn('setStatusBarStyle', e?.message ?? e),
    );
  }
};

const applyDarkMode = (dark: boolean) => {
  darkMode.value = dark;
  document.documentElement.classList.toggle(DARK_PALETTE_CLASS, dark);
  syncSurroundings(dark);
};

const applyPreference = () => {
  applyDarkMode(
    preference === ThemePreference.DARK ||
      (preference === ThemePreference.SYSTEM && !!systemQuery?.matches),
  );
};

/**
 * Applies the last saved preference, or light if there is none, following the
 * system colour scheme when that is the choice. Runs before the store is hydrated; the store's
 * preference is then passed to setThemePreference.
 */
export const initTheme = () => {
  preference = readStoredPreference();

  systemQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
  systemQuery?.addEventListener('change', () => {
    if (preference === ThemePreference.SYSTEM) applyPreference();
  });

  applyPreference();
};

/**
 * Applies and saves a light, dark or follow-the-system preference, and returns
 * the preference actually adopted, which is light if the one given is not
 * a preference.
 */
export const setThemePreference = (next: ThemePreference): ThemePreference => {
  preference = toPreference(next);
  storePreference(preference);
  applyPreference();

  return preference;
};
